import "react-app-polyfill/stable";

import React from "react";
import { createRoot } from "react-dom/client";
import "animate.css/animate.min.css";

import reportWebVitals from "./utils/reportWebVitals";
import blockNumberInputWheel from "./utils/blockNumberInputWheel";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LicenseInfo } from "@mui/x-license";

import App from "./App";

// LWI-1088 — Vite emits `vite:preloadError` when a dynamic import 404s,
// which is what happens after a deploy: the user's cached HTML references
// hashed chunk filenames that no longer exist on the server. Force a full
// reload to pull down fresh HTML + fresh chunk refs. The 10s guard
// (via sessionStorage) prevents an infinite loop if the new deploy is
// genuinely broken and still emits the same error after reloading.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", () => {
    const KEY = "vite-preload-last-reload";
    const now = Date.now();
    const last = Number(sessionStorage.getItem(KEY)) || 0;
    if (now - last < 10_000) return;
    sessionStorage.setItem(KEY, String(now));
    window.location.reload();
  });
}

// LWI-1432 — a focused <input type="number"> is edited by the mouse wheel /
// two-finger trackpad scroll, so scrolling the page after clicking into Unit
// Cost silently changed the value. Installed once for every number input in
// the app rather than per-field.
if (typeof window !== "undefined") {
  blockNumberInputWheel(window);
}

if (import.meta.env.VITE_MUI_LICENSE_KEY) {
  LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);
}

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <ThemeProvider>
    <App />
  </ThemeProvider>
);

// LWI-699 — defer Sentry + Datadog RUM until after first paint and
// dynamic-import them so they ship in a separate chunk (not the main
// bundle). Tradeoff: errors that fire during initial parse won't be
// captured. Acceptable for an authenticated SPA where the expensive
// paint is the first render of the dashboard layout.
async function initObservability() {
  const [Sentry, { datadogRum }] = await Promise.all([
    import("@sentry/react"),
    import("@datadog/browser-rum"),
  ]);

  Sentry.init({
    dsn: "https://1fbd209b26c6bb2390ddea59def644ef@o4508257772503040.ingest.us.sentry.io/4510747357413376",
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
    // Enable logs to be sent to Sentry
    enableLogs: true,
  });

  // Derive the API origin from VITE_API_URL (which includes "/api/v1/") so the
  // same build correlates RUM sessions with backend traces across dev, staging,
  // and prod without hardcoding hostnames.
  const apiOrigin = (import.meta.env.VITE_API_URL || "").replace(/\/api\/v1\/?$/, "");

  datadogRum.init({
    applicationId: "3951df8b-118d-4759-a517-134bca800eb8",
    clientToken: "pub1bf88e4c9bbf40c50af29d7e26d3ae95",
    site: "ddog-gov.com",
    service: import.meta.env.VITE_DD_SERVICE,
    env: import.meta.env.VITE_DD_ENV,
    version: "1.0.0",
    // LWI-1115 — capture everything: at ~400 sessions/week, sampling saved
    // nothing meaningful while halving audit/support visibility.
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: "allow",
    trackingConsent: "granted",
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    enablePrivacyForActionName: true,
    allowedTracingUrls: apiOrigin ? [apiOrigin] : [],
  });
}

if (typeof window !== "undefined" && "requestIdleCallback" in window) {
  window.requestIdleCallback(initObservability, { timeout: 2000 });
} else {
  setTimeout(initObservability, 0);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
